import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import compact from 'lodash/compact';
import sumBy from 'lodash/sumBy';
import {
    Block,
    FlexDiv,
    Heading3,
    palette,
    SpaceBetweenDiv,
    TextDefault,
    TextInput,
    TextTiny,
    ThemedButton,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import SelectEmployees from '../../components/manager/wizard/select-employees';
import { useStores } from '../../hooks/use-stores';
import AwareExposeSubNav from './AwareExposeSubNav';

const FieldLabel = styled.label`
    font-size: 11px;
    font-weight: 600;
    color: ${palette.graphite.charcoal};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    display: block;
    margin-top: 8px;
    margin-bottom: 6px;
`;

const StepCard = styled(Block)`
    margin-bottom: 14px;
`;

const StepNumber = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${palette.evergreen.aspargus};
    color: #fff;
    font-weight: 700;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const InfoAlert = styled.div`
    background: ${palette.pastel.aspargus};
    color: ${palette.evergreen.aspargus};
    border: 1px solid ${palette.evergreen.aspargus};
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 12px;
    line-height: 1.5;
    margin-top: 12px;
`;

const WarnAlert = styled.div`
    background: ${palette.pastel.orange};
    color: ${palette.vibrant.coral};
    border: 1px solid ${palette.vibrant.orange};
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 12px;
    line-height: 1.5;
    margin-top: 12px;
`;

const MessageTextarea = styled.textarea`
    display: block;
    width: 100%;
    box-sizing: border-box;
    min-height: 160px;
    padding: 12px;
    margin: 0;
    font-size: 14px;
    font-family: inherit;
    line-height: 1.5;
    color: ${palette.graphite.heavyMetal};
    background: ${palette.graphite.white};
    border: 1px solid ${palette.graphite.silver};
    border-radius: 4px;
    resize: vertical;

    &:focus {
        outline: none;
        border-color: ${palette.evergreen.aspargus};
    }
`;

const EmailFields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-top: 8px;
`;

const DEFAULT_EMAIL_SUBJECT =
    'AwareGO: please complete your exposure risk assessment';
const DEFAULT_EMAIL_MESSAGE =
    "Hi {{name}},\n\nAs part of our security awareness programme we're running a personal exposure risk assessment using AwareExpose. It takes about 5 minutes and helps us understand how visible you are to potential attackers — and how well protected you are.\n\nYour results are confidential and used only to improve our organisation's security posture.";

function NewAwareExposeCampaign() {
    const navigate = useNavigate();
    const {
        store,
        listStore,
        userStore,
        awareExposeStore,
        commonStore,
    } = useStores();
    const { currentCompany } = store;
    const { listsWithUsers: lists, error: listError } = listStore;
    const { loadingUsers } = userStore;
    const { savingCampaign, launchingCampaign } = awareExposeStore;

    const [name, setName] = useState('');
    const [selectedListIds, setSelectedListIds] = useState([]);
    const [deadline, setDeadline] = useState('');
    const [emailSubject, setEmailSubject] = useState(
        DEFAULT_EMAIL_SUBJECT
    );
    const [emailMessage, setEmailMessage] = useState(
        DEFAULT_EMAIL_MESSAGE
    );
    const [addEmployeeModal, setAddEmployeeModal] = useState(false);
    const [recipientsModal, setRecipientsModal] = useState(false);

    const companyId = currentCompany && currentCompany.company_id;
    const createListDisabled =
        !currentCompany || !currentCompany.canAddUsers;

    useEffect(() => {
        if (companyId) {
            listStore.loadCompanyLists(companyId);
        }
    }, [companyId, listStore]);

    const toggleUserListSelected = useCallback((listId) => {
        setSelectedListIds((prev) =>
            prev.includes(listId)
                ? prev.filter((x) => x !== listId)
                : [...prev, listId]
        );
    }, []);

    const userListsSelected = useMemo(
        () =>
            compact(
                selectedListIds.map((id) => listStore.allListsIndex[id])
            ),
        [selectedListIds, listStore.lists]
    );

    const selectedUserCount = useMemo(
        () =>
            sumBy(userListsSelected, (l) => Number(l.usersCount) || 0),
        [userListsSelected]
    );

    const campaignDraft = useMemo(
        () => ({
            selectedUserListsIds: selectedListIds,
            selectedUserCount,
            userListsSelected,
        }),
        [selectedListIds, selectedUserCount, userListsSelected]
    );

    const buildPayload = () => ({
        name,
        list_ids: selectedListIds.map(Number),
        email_subject: emailSubject,
        email_message: emailMessage,
        deadline_at: deadline || null,
    });

    const canLaunch =
        !!name &&
        selectedListIds.length > 0 &&
        selectedUserCount > 0 &&
        !savingCampaign &&
        !launchingCampaign;

    const showAddEmployeeModal = () => setAddEmployeeModal(true);
    const hideAddEmployeeModal = () => setAddEmployeeModal(false);
    const showRecipientsModal = () => setRecipientsModal(true);
    const hideRecipientsModal = () => setRecipientsModal(false);

    const requestUsersForList = (listId) => {
        if (companyId) userStore.loadUsers(companyId, listId, true);
    };

    const createRecipientsList = async (list) => {
        list.users = (list.users || []).filter((x) => x.email);
        const result = await listStore.save(companyId, null, list);
        if (result) hideRecipientsModal();
        toggleUserListSelected(result);
        if (!addEmployeeModal) showAddEmployeeModal();
    };

    const saveDraft = async () => {
        if (!name) {
            commonStore.error('Please enter a campaign name');
            return;
        }
        if (!selectedListIds.length) {
            commonStore.error('Please select at least one employee list');
            return;
        }
        try {
            const c = await awareExposeStore.createCampaign(
                companyId,
                buildPayload()
            );
            commonStore.success('Draft campaign saved');
            navigate(`/aware-expose/campaigns/${c.id}`);
        } catch (e) {
            commonStore.error(
                awareExposeStore.error || 'Failed to save campaign'
            );
        }
    };

    const launch = async () => {
        if (!canLaunch) return;
        try {
            const c = await awareExposeStore.createCampaign(
                companyId,
                buildPayload()
            );
            const launched = await awareExposeStore.launchCampaign(
                companyId,
                c.id
            );
            commonStore.success(
                `Campaign launched — ${
                    launched.audience_size || 0
                } invitations queued.`
            );
            navigate(`/aware-expose/campaigns/${c.id}`);
        } catch (e) {
            commonStore.error(
                awareExposeStore.error || 'Failed to launch campaign'
            );
        }
    };

    return (
        <Container className="manager-content">
            <Helmet>
                <title>New AwareExpose campaign</title>
            </Helmet>
            <HeaderSection
                heading={'New AwareExpose campaign'}
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'Set up and launch a personal exposure assessment for a group of employees.'
                            }
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <Link
                        to={'/aware-expose/campaigns'}
                        style={{ fontSize: 13 }}
                    >
                        ← Back to campaigns
                    </Link>
                }
            />
            <AwareExposeSubNav active="campaigns" />

            <StepCard padding="20" column>
                <FlexDiv alignCenter gap={12}>
                    <StepNumber>1</StepNumber>
                    <Heading3>Campaign details</Heading3>
                </FlexDiv>
                <TextTiny lighter>
                    Basic information about this exposure check.
                </TextTiny>

                <FieldLabel>Campaign name</FieldLabel>
                <TextInput
                    fullWidth
                    placeholder={'e.g. Q3 2026 — All staff'}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />

                <FieldLabel>Response deadline</FieldLabel>
                <TextInput
                    type="date"
                    fullWidth
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                />
            </StepCard>

            <StepCard tablePadding column>
                <FlexDiv alignCenter gap={12} style={{ marginBottom: 8 }}>
                    <StepNumber>2</StepNumber>
                    <Heading3>Target audience</Heading3>
                </FlexDiv>
                <TextTiny lighter>
                    Select one or more employee lists — same flow as
                    training and assessment wizards.
                </TextTiny>
                <SelectEmployees
                    itemString="campaign"
                    item={campaignDraft}
                    lists={lists}
                    toggleUserListSelected={toggleUserListSelected}
                    ongoing={false}
                    addEmployeeModal={addEmployeeModal}
                    showAddEmployeeModal={showAddEmployeeModal}
                    hideAddEmployeeModal={hideAddEmployeeModal}
                    recipientsModal={recipientsModal}
                    showRecipientsModal={showRecipientsModal}
                    hideRecipientsModal={hideRecipientsModal}
                    error={listError}
                    loadingUsers={loadingUsers}
                    requestUsersForList={requestUsersForList}
                    selectedLists={selectedListIds}
                    selectedUserCount={selectedUserCount}
                    disabled={createListDisabled}
                    createRecipientsList={createRecipientsList}
                    currentCompany={currentCompany}
                />
            </StepCard>

            <StepCard padding="20" column>
                <FlexDiv alignCenter gap={12}>
                    <StepNumber>3</StepNumber>
                    <Heading3>Invitation email</Heading3>
                </FlexDiv>
                <TextTiny lighter>
                    What employees will see when they receive the
                    invitation. <code>{'{{name}}'}</code> and{' '}
                    <code>{'{{deadline}}'}</code> are replaced per
                    recipient.
                </TextTiny>

                <EmailFields>
                    <FieldLabel>Email subject</FieldLabel>
                    <TextInput
                        fullWidth
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                    />

                    <FieldLabel>Email message</FieldLabel>
                    <MessageTextarea
                        value={emailMessage}
                        onChange={(e) => setEmailMessage(e.target.value)}
                        rows={8}
                        spellCheck
                    />
                </EmailFields>

                <InfoAlert>
                    Employees receive a unique link in their email. They
                    can complete the assessment without logging in — but
                    each result is bound to their invite, so you'll see
                    per-employee scores in this dashboard.
                </InfoAlert>
            </StepCard>

            <StepCard padding="20" column>
                <FlexDiv alignCenter gap={12}>
                    <StepNumber>4</StepNumber>
                    <Heading3>Review &amp; launch</Heading3>
                </FlexDiv>

                {selectedListIds.length === 0 ? (
                    <WarnAlert>
                        Please select at least one employee list before
                        launching.
                    </WarnAlert>
                ) : (
                    <InfoAlert>
                        This will send {selectedUserCount} email
                        invitation
                        {selectedUserCount === 1 ? '' : 's'} across{' '}
                        {selectedListIds.length} list
                        {selectedListIds.length === 1 ? '' : 's'}. Make
                        sure your campaign details and message look right
                        before launching.
                    </InfoAlert>
                )}

                <SpaceBetweenDiv style={{ marginTop: 16 }}>
                    <FlexDiv gap={12}>
                        <ThemedButton
                            text={
                                launchingCampaign
                                    ? 'Launching…'
                                    : 'Launch campaign'
                            }
                            mode={'primary'}
                            disabled={!canLaunch}
                            onClick={launch}
                            $noWrap
                        />
                        <ThemedButton
                            text={
                                savingCampaign ? 'Saving…' : 'Save as draft'
                            }
                            mode={'secondary'}
                            disabled={!name || savingCampaign}
                            onClick={saveDraft}
                            $noWrap
                        />
                    </FlexDiv>
                    <TextDefault lighter>
                        You can edit or delete drafts before launching.
                    </TextDefault>
                </SpaceBetweenDiv>
            </StepCard>
        </Container>
    );
}

export default observer(NewAwareExposeCampaign);
